import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import {
  useExportAnalysisAsCSV,
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import {
  DEFAULT_COMPARE_DURATION_A_NAME,
  DEFAULT_COMPARE_DURATION_B_NAME,
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
} from '@modules/quantify/constants';
import { getBreakdownColumns } from '@modules/quantify/hooks/useExportQuantifyReportDataAsCSV/helpers/getBreakdownColumns';
import { getCompareAnalysisColumns } from '@modules/quantify/hooks/useExportQuantifyReportDataAsCSV/helpers/getCompareAnalysisColumns';
import { getTimeSeriesAnalysisColumns } from '@modules/quantify/hooks/useExportQuantifyReportDataAsCSV/helpers/getTimeSeriesAnalysisColumns';
import { useGetQuantifyTableEQLProps } from '@modules/quantify/hooks/useFetchQuantifyTableData/useGetQuantifyTableEQLProps';
import { useGetQuantifyFormulaDisplayName } from '@modules/quantify/hooks/useGetQuantifyFormulaDisplayName';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { ExportCsvColumn, QuantifyVisualisationType } from 'generated/graphql';
import { useCallback } from 'react';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { useGetUser } from 'v2/core/services/user';

export const useExportQuantifyReportDataAsCSV = () => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const eqlProps = useGetQuantifyTableEQLProps({
    shouldSkipPaginateFunction: true,
    shouldSkipSearchFunction: true,
  });
  const { eql } = useGetEQLExpression({
    tenantID,
    eqlProps,
  });

  const analysisTitle = useAnalysisStore(({ title }) => title);
  const compareFilterAName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.baseQueryName || DEFAULT_COMPARE_FILTER_A_NAME,
  );
  const compareFilterBName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.deltaQueryName || DEFAULT_COMPARE_FILTER_B_NAME,
  );
  const compareDurationAName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.baseQueryName || DEFAULT_COMPARE_DURATION_A_NAME,
  );
  const compareDurationBName = useQuantifyStore(
    ({ query }) =>
      query.compareMetadata?.deltaQueryName || DEFAULT_COMPARE_DURATION_B_NAME,
  );

  const groupBy = useQuantifyStore(({ query }) => query.groupByField);
  const activeVisualisationType = useQuantifyStore(
    ({ query }) => query.visualisationType,
  );
  const aggregatePeriod = useQuantifyStore(
    ({ query }) => query.aggregatePeriod,
  );
  const quantifyChartDataPoints = useQuantifyStore(
    ({ reportData }) => reportData.quantify.chartDataPoints,
  );
  const compareTo = useQuantifyStore(({ query }) => query.compareQuery);
  const formulaID = useQuantifyStore(
    ({ query }) => query.formulaQuery?.eqlFunction,
  );
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.formulaQuery?.criteria,
  );
  const { formulaDisplayName } = useGetQuantifyFormulaDisplayName({
    shouldIncludeGroupByName: true,
  });

  const [exportAnalysisAsCSV, { data, error, status, called, loading }] =
    useExportAnalysisAsCSV();

  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const getCSVColumns = useCallback((): ExportCsvColumn[] => {
    if (compareTo) {
      const compareLabelA = compareTo.whereClause
        ? compareFilterAName
        : compareDurationAName;
      const compareLabelB = compareTo.whereClause
        ? compareFilterBName
        : compareDurationBName;

      return getCompareAnalysisColumns({
        compareTo,
        formulaID,
        groupBy,
        measuredAsCriteria,
        compareFieldNames: {
          fieldAName: compareLabelA,
          fieldBName: compareLabelB,
        },
        shouldRemoveFileUploadPrefix,
        isUnifiedFieldEnabled,
      });
    }

    if (
      activeVisualisationType === QuantifyVisualisationType.Bar ||
      activeVisualisationType === QuantifyVisualisationType.Pie
    ) {
      return getBreakdownColumns({
        formulaID,
        groupBy,
        formulaDisplayName,
        shouldRemoveFileUploadPrefix,
        isUnifiedFieldEnabled,
      });
    }

    if (activeVisualisationType === QuantifyVisualisationType.Trend) {
      return getTimeSeriesAnalysisColumns({
        aggregatePeriod,
        formulaID,
        groupBy,
        quantifyChartDataPoints,
        formulaDisplayName,
        shouldRemoveFileUploadPrefix,
        isUnifiedFieldEnabled,
      });
    }

    return [];
  }, [
    compareTo,
    activeVisualisationType,
    compareFilterAName,
    compareDurationAName,
    compareFilterBName,
    compareDurationBName,
    formulaID,
    groupBy,
    measuredAsCriteria,
    formulaDisplayName,
    aggregatePeriod,
    quantifyChartDataPoints,
    shouldRemoveFileUploadPrefix,
    isUnifiedFieldEnabled,
  ]);

  const exportQuantifyReportDataAsCSV = useCallback(async () => {
    if (!eql) {
      return;
    }
    await exportAnalysisAsCSV({
      expression: eql,
      filename: `${analysisTitle} - `,
      columns: getCSVColumns(),
    });
  }, [analysisTitle, eql, exportAnalysisAsCSV, getCSVColumns]);

  return [
    exportQuantifyReportDataAsCSV,
    { data, error, status, called, loading },
  ] as const;
};
